import React, { useEffect } from "react";
import { SxProperties } from "../CustomComponents";
import { JSBackgroundSizeComponent } from "./JSInputComponents/JSBackgroundSize";
import { JSColorInputComponent } from "./JSInputComponents/JSColorInput";
import { JSDisplayComponent } from "./JSInputComponents/JSDisplayComponent";
import { JSBorderStyleComponent } from "./JSInputComponents/JSBorderStyle";
import { JSPointerEventsComponent } from "./JSInputComponents/PointerEvents";
import { JSPositionComponent } from "./JSInputComponents/JSPositionComponent";
import { JSBoxShadowComponent } from "./JSInputComponents/BoxShadow";
import { JSFontFamilyComponent } from "./JSInputComponents/JSFontFamilyComponent";
import { JSJustifyAndAlignComponent } from "./JSInputComponents/JSJustifyAndAlignComponent";
import { JSAlignSelfComponent } from "./JSInputComponents/JSAlignSelfComponent";
import { JSTextDecorationComponent } from "./JSInputComponents/JSTextDecorationComponent";
import { JSFontStyleComponent } from "./JSInputComponents/JSFontStyleComponent";
import { JSOpacityComponent } from "./JSInputComponents/JSOpacityComponent";
import { JSFontSizeComponent } from "./JSInputComponents/JSFontSizeComponent";
import { JSFontWeightComponent } from "./JSInputComponents/JSFontWeightComponent";
import { JSPaddingAndMarginComponent } from "./JSInputComponents/JSPaddingAndMarginComponent";
import { JSTextAlignComponent } from "./JSInputComponents/JSTextAlignComponent";
import { JSLineHeightComponent } from "./JSInputComponents/JSLineHeight";
import { JSBorderComponent } from "./JSInputComponents/JSBorderCompontent";
import { JSBorderRadiusComponent } from "./JSInputComponents/JSBorderRadiusComponent";
import FormText from "../../../../../components/Forms/FormText";
import { JSBackgroundRepeatComponent } from "./JSInputComponents/JSBackgroundRepeatComponent";
import { JSBackgroundImageComponent } from "./JSInputComponents/JSBackgroundImage";
import { JSTranslateComponent } from "./JSInputComponents/JSTranslateComponent";
import { useFormContext } from "react-hook-form";
import { JSScaleComponent } from "./JSInputComponents/JSScaleComponent";
import { JSZoomComponent } from "./JSInputComponents/JSZoomInputComponent";
import { JSFlexDirectionComponent } from "./JSInputComponents/JSFlexDirectionComponent";
import { JSAlignItemsComponent } from "./JSInputComponents/JSAlignItemsComponent";
import { JSTextTransformComponent } from "./JSInputComponents/JSTextTransform";
import { JSVisibilityComponent } from "./JSInputComponents/JSVisibilityComponent";
import { JSBorderWidthComponent } from "./JSInputComponents/JSBorderWidth";

export type JSInputProps = { name: string; fonts: string[]; cssProperty: SxProperties; required: boolean };
export type JSInputComponent = { handlesCssProperties: SxProperties[]; Component: React.FC<JSInputProps> };

const JSFallbackComponent: JSInputComponent = {
  Component: ({ name, required }: JSInputProps) => <FormText name={name} rules={{ required }} />,
  handlesCssProperties: [
    "textShadow",
    "borderWidth",
    "width",
    "height",
    "minHeight",
    "minWidth",
    "WebkitLineClamp",
    "transform",
    "gap",
  ],
};

class JSCSSInputComponents {
  components: JSInputComponent[] = [];

  registerComponent(component: JSInputComponent) {
    component.Component = React.memo(component.Component);
    this.components.push(component);
  }

  getComponentForCssProperty(property: SxProperties) {
    const component = this.components.find((c) => c.handlesCssProperties.includes(property));
    if (component == null) {
      return function JSErrorMissingCSSProperty() {
        return <>{`Missing input component for css property "${property}"`}</>;
      };
    }
    return component.Component;
  }
}

const inputs = new JSCSSInputComponents();
inputs.registerComponent(JSBackgroundSizeComponent);
inputs.registerComponent(JSColorInputComponent);
inputs.registerComponent(JSDisplayComponent);
inputs.registerComponent(JSVisibilityComponent);
inputs.registerComponent(JSBorderStyleComponent);
inputs.registerComponent(JSPointerEventsComponent);
inputs.registerComponent(JSPositionComponent);
inputs.registerComponent(JSScaleComponent);
inputs.registerComponent(JSZoomComponent);
inputs.registerComponent(JSBoxShadowComponent);
inputs.registerComponent(JSJustifyAndAlignComponent);
inputs.registerComponent(JSAlignSelfComponent);
inputs.registerComponent(JSAlignItemsComponent);
inputs.registerComponent(JSOpacityComponent);
inputs.registerComponent(JSPaddingAndMarginComponent);
inputs.registerComponent(JSTextAlignComponent);
inputs.registerComponent(JSFlexDirectionComponent);
inputs.registerComponent(JSLineHeightComponent);
inputs.registerComponent(JSBorderWidthComponent);
inputs.registerComponent(JSBorderComponent);
inputs.registerComponent(JSBorderRadiusComponent);
inputs.registerComponent(JSBackgroundRepeatComponent);
inputs.registerComponent(JSBackgroundImageComponent);
inputs.registerComponent(JSTranslateComponent);
inputs.registerComponent(JSTextTransformComponent);

// font
inputs.registerComponent(JSFontFamilyComponent);
inputs.registerComponent(JSTextDecorationComponent);
inputs.registerComponent(JSFontStyleComponent);
inputs.registerComponent(JSFontSizeComponent);
inputs.registerComponent(JSFontWeightComponent);

// components that don't have any specific logic (yet?)
inputs.registerComponent(JSFallbackComponent);

function _JamezzStylesCSSInput({
  JSClassName,
  valueKey,
  fonts,
  overrideValue,
  required = false,
}: {
  JSClassName: string;
  valueKey: SxProperties;
  fonts: string[];
  overrideValue?: string | undefined;
  required: boolean;
}) {
  const CSSInputComponent = inputs.getComponentForCssProperty(valueKey);
  const name = `custom_component_styling.${JSClassName}.${valueKey}`;

  const { setValue } = useFormContext();

  useEffect(() => {
    if (overrideValue) setValue(name, overrideValue);
  }, [overrideValue]);

  return <CSSInputComponent cssProperty={valueKey} fonts={fonts} name={name} required={required} />;
}

export const JamezzStylesCSSInput = React.memo(_JamezzStylesCSSInput);
